import { FC, ReactElement, ReactNode, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { Affiliate, AffiliateController, AffiliateService } from "./Affiliate";
import { Box, Card, Container, Grid, VBox } from "@/components-v2/Box";
import {
  dcompanyFrontpageUrl,
  navigateToFrontpage,
  navigateToNorwegianBankIdLogin,
} from "@/utils/navigation";
import { DcompanyLogoImg } from "@/components-v2/Logo";
import StyreforeningenLogoImage from "@/assets/img/brand/styreforeningen_logo.svg";
import { HiddenForMobile, VisibleOnMobile } from "@/components-v2/Mobile";
import { Button } from "@/components-v2/Button";
import { isCancelled, isFailure, isPending } from "@/common/common";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import { Icon } from "@/components-v2/Icon";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { changeLanguageAction } from "@/i18n/i18nDomain";
import {
  useDispatch,
  useMessageStore,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import { Markdown } from "@/text/Markdown.tsx";

const AffiliatePageInitialiser: FC<{ affiliateKey: string }> = observer(({ affiliateKey }) => {
  const { http } = useRootStore();
  const controller = useMemo(
    () => new AffiliateController(affiliateKey, new AffiliateService(http)),
    [affiliateKey, http],
  );
  useEffect(() => {
    controller.fetchAffiliate();
  }, [controller]);
  if (
    !controller.affiliateResult ||
    isPending(controller.affiliateResult) ||
    isCancelled(controller.affiliateResult)
  ) {
    return <LoadingIndicator />;
  } else if (isFailure(controller.affiliateResult)) {
    return <AffiliateErrorPage />;
  } else {
    return <AffiliatePage affiliate={controller.affiliateResult.data} />;
  }
});

export const AffiliateErrorPage = observer(function AffiliateErrorPage() {
  const { t } = useTranslation();
  return (
    <FullScreenLayout
      appBar={
        <AffiliateHeader logo={<DCompanyLogo />} loginEvent={navigateToFrontpage} subHeader={""} />
      }
      centerPanel={
        <Container maxWidth={"md"} paddingTop gap={"lg"}>
          <Alert type={"error"} message={t("affiliate.get.error")} />
          <LoginBox loginHandler={navigateToFrontpage} />
        </Container>
      }
    />
  );
});

export const AffiliatePage: FC<{
  affiliate: Affiliate;
}> = observer(({ affiliate }) => {
  const { t } = useTranslation();
  const alertStore = useMessageStore();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(changeLanguageAction(affiliate.language));
  }, [affiliate.language, dispatch]);

  return (
    <FullScreenLayout
      appBar={
        <AffiliateHeader
          loginEvent={navigateToNorwegianBankIdLogin}
          logo={<StyreforeningenLogo />}
          subHeader={t("affiliatepage.subheader.styreforeningen")}
        />
      }
      alertStore={alertStore}
      centerPanel={<AffiliateBody loginHandler={navigateToNorwegianBankIdLogin} />}
    />
  );
});

const AffiliateBody: FC<{
  loginHandler: () => void;
}> = observer(({ loginHandler }) => {
  return (
    <Container maxWidth={"lg"} paddingTop={"lg"} alignItems={"center"}>
      <Grid cols={1} colsMd={2} gap={"xl"} paddingTop={"lg"} alignItems={"center"}>
        <AffiliateBox />
        <LoginBox loginHandler={loginHandler} />
      </Grid>
    </Container>
  );
});

const AffiliateBox = () => {
  const { t } = useTranslation();
  return (
    <VBox>
      <h3>{t("affiliatepage.affiliateDescription.header")}</h3>
      <p>{t("affiliatepage.affiliateDescription.description")}</p>
    </VBox>
  );
};

const LoginBox: FC<{
  loginHandler: () => void;
}> = observer(({ loginHandler }) => {
  const { t } = useTranslation();
  return (
    <Card padding={"lg"} gap={"lg"}>
      <Box>
        <h3>{t("affiliatepage.gettingStarted.header")}</h3>
      </Box>
      <ListItem>{t("affiliatepage.gettingStarted.item1")}</ListItem>
      <ListItem>{t("affiliatepage.gettingStarted.item2")}</ListItem>
      <Button
        variant={"primary"}
        fullWidth
        label={t("affiliatepage.login.bankid")}
        onClick={loginHandler}
      />
    </Card>
  );
});

export const AffiliateHeader: FC<{
  loginEvent: () => void;
  logo: ReactElement;
  subHeader: string;
}> = observer(({ loginEvent, logo, subHeader }) => {
  const { t } = useTranslation();
  return (
    <>
      <VisibleOnMobile>
        <Box justifyContent={"center"} borderBottom padding>
          {logo}
        </Box>
      </VisibleOnMobile>
      <HiddenForMobile>
        <Box justifyContent={"space-between"} alignItems={"center"} borderBottom padding>
          <VBox grow gap={false}>
            {logo}
            <div>{subHeader}</div>
          </VBox>
          <Box alignItems={"center"} justifyContent={"end"} gap={"lg"}>
            <Markdown value={t("affiliatepage.contactSales")} enableLinks />
            <Markdown value={t("about-dcompany")} enableLinks />
            <Button
              label={t("affiliatepage.login")}
              onClick={loginEvent}
              variant={"secondary"}
              size={"sm"}
            />
          </Box>
        </Box>
      </HiddenForMobile>
    </>
  );
});

const DCompanyLogo = () => {
  const { lang } = useTranslation();
  return (
    <div>
      <a href={dcompanyFrontpageUrl(lang)}>
        <DcompanyLogoImg full style={{ maxWidth: 200 }} />
      </a>
    </div>
  );
};

const StyreforeningenLogo = () => {
  const { lang } = useTranslation();
  return (
    <div>
      <a href={dcompanyFrontpageUrl(lang)}>
        <img src={StyreforeningenLogoImage} alt="dCompany logo" style={{ maxWidth: 200 }} />
      </a>
    </div>
  );
};

export const ListItem = observer(({ children }: { children: ReactNode }) => {
  return (
    <Box alignItems={"center"} wrap={false}>
      <Icon name={"check"} />
      <div>{children}</div>
    </Box>
  );
});

export default AffiliatePageInitialiser;
